import { useState, useEffect, useMemo } from 'react';
import { useFormikContext } from 'formik';
import { Select, Input, FormGroup, Label } from 'shared-components';
import {
  GOOGLE_FONTS,
  SYSTEM_FONTS,
  CUSTOM_FONT_VALUE,
  classifyFont,
  googleFontUrl,
  googleFontCssValue,
} from '@topshots/core-models';
import styles from './settings.module.css';

interface FontSelectorProps {
  fieldName: string;
}

export const FontSelector = ({ fieldName }: FontSelectorProps) => {
  const { setFieldValue, values } = useFormikContext<Record<string, unknown>>();

  const currentFontValue: string =
    (fieldName
      .split('.')
      .reduce(
        (obj, key) => (obj as Record<string, unknown>)?.[key],
        values as unknown
      ) as string) ?? '';

  const classification = useMemo(
    () => classifyFont(currentFontValue),
    [currentFontValue]
  );

  const [isCustom, setIsCustom] = useState(classification.type === 'custom');

  // Sync isCustom when the value changes externally (data load, reset to defaults)
  useEffect(() => {
    setIsCustom(classification.type === 'custom');
  }, [classification.type]);

  const selectValue = useMemo(() => {
    if (isCustom) return CUSTOM_FONT_VALUE;
    if (classification.type === 'google') return classification.entry.name;
    if (classification.type === 'system') return classification.entry.value;
    return CUSTOM_FONT_VALUE;
  }, [isCustom, classification]);

  const previewFontFamily = useMemo(() => {
    if (classification.type === 'google') {
      return googleFontCssValue(classification.entry);
    }
    return currentFontValue;
  }, [classification, currentFontValue]);

  // Load Google Font for admin preview
  useEffect(() => {
    if (classification.type !== 'google') return;
    const fontName = classification.entry.name;
    const linkId = `admin-font-preview-${fontName.replace(/\s/g, '-')}`;
    if (document.getElementById(linkId)) return;

    const link = document.createElement('link');
    link.id = linkId;
    link.rel = 'stylesheet';
    link.href = googleFontUrl(fontName);
    document.head.appendChild(link);
  }, [classification]);

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    if (val === CUSTOM_FONT_VALUE) {
      setIsCustom(true);
      return;
    }
    setIsCustom(false);
    const google = GOOGLE_FONTS.find((f) => f.name === val);
    if (google) {
      setFieldValue(fieldName, google.name);
    } else {
      setFieldValue(fieldName, val);
    }
  };

  const sansSerif = GOOGLE_FONTS.filter((f) => f.category === 'sans-serif');
  const serif = GOOGLE_FONTS.filter((f) => f.category === 'serif');
  const display = GOOGLE_FONTS.filter((f) => f.category === 'display');

  return (
    <FormGroup className={styles.fontField}>
      <Label htmlFor={fieldName}>Font</Label>
      <Select value={selectValue} onChange={handleSelectChange}>
        <optgroup label="System Fonts">
          {SYSTEM_FONTS.map((f) => (
            <option key={f.value} value={f.value}>
              {f.name}
            </option>
          ))}
        </optgroup>
        <optgroup label="Sans-Serif">
          {sansSerif.map((f) => (
            <option key={f.name} value={f.name}>
              {f.name}
            </option>
          ))}
        </optgroup>
        <optgroup label="Serif">
          {serif.map((f) => (
            <option key={f.name} value={f.name}>
              {f.name}
            </option>
          ))}
        </optgroup>
        <optgroup label="Display">
          {display.map((f) => (
            <option key={f.name} value={f.name}>
              {f.name}
            </option>
          ))}
        </optgroup>
        <option value={CUSTOM_FONT_VALUE}>Custom...</option>
      </Select>

      {isCustom && (
        <Input
          type="text"
          placeholder="e.g. 'Comic Sans MS', cursive"
          value={currentFontValue}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setFieldValue(fieldName, e.target.value)
          }
          className={styles.fontCustomInput}
        />
      )}

      <div
        className={styles.fontPreview}
        style={{ fontFamily: previewFontFamily }}
      >
        The quick brown fox jumps over the lazy dog
      </div>
    </FormGroup>
  );
};
